// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/WorkflowExecutionTab.jsx

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionMetricTile from "../shared/ExecutionMetricTile";
import ExecutionTimeline from "../shared/ExecutionTimeline";
import ExecutionActionRow from "../shared/ExecutionActionRow";
import ExecutionEmptyState from "../shared/ExecutionEmptyState";
import ExecutionContextCard from "../shared/ExecutionContextCard";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function humanizeValue(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "risk queue default": "Risk Queue Default",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "in progress": "In Progress",
    "review required": "Review Required",
    "approval waiting": "Approval Waiting",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
    "ready for validation": "Ready for Validation",
    "manual execution": "Manual Execution",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function normalizeItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.history)) return value.history;
  if (Array.isArray(value?.timeline)) return value.timeline;
  return [];
}

function statusTone(value) {
  const normalized = clean(value).toLowerCase();
  if (["failed", "blocked", "review_required", "cancelled"].includes(normalized)) return "danger";
  if (["completed", "complete", "passed", "linked"].includes(normalized)) return "ok";
  if (["queued", "waiting", "assigned", "executing", "in_progress", "awaiting_validation", "awaiting_approval"].includes(normalized)) return "default";
  return "neutral";
}

function itemTitle(item = {}, fallback = "Workflow Item") {
  return displayValue(item.name || item.title || item.step_key || item.dependency_key || item.approval_key || item.event_type, fallback);
}

function itemContext(item = {}, fallback = "No additional workflow context was provided.") {
  return displayValue(
    item.description ||
      item.message ||
      item.blocking_reason ||
      item.transition_reason ||
      item.request_reason ||
      item.decision_reason ||
      item.metadata?.message ||
      item.payload?.message,
    fallback
  );
}

function dependencyStatus(item = {}) {
  if (item.is_satisfied === true) return "Satisfied";
  if (item.is_satisfied === false) return "Blocked";
  return humanizeValue(item.status, "Unknown");
}

export default function WorkflowExecutionTab({
  task = {},
  workflowEvents = [],
  workflowContext = null,
  workflowLoading = false,
  workflowError = "",
  onRefreshWorkflow,
  onOpenTab,
}) {
  const workflow = workflowContext?.workflow && typeof workflowContext.workflow === "object" ? workflowContext.workflow : {};
  const steps = normalizeItems(workflowContext?.steps);
  const dependencies = normalizeItems(workflowContext?.dependencies);
  const approvals = normalizeItems(workflowContext?.approvals);
  const contextHistory = normalizeItems(workflowContext?.history);
  const timelineEvents = contextHistory.length ? contextHistory : normalizeItems(workflowEvents);

  const status = clean(workflowContext?.status || workflow.status || task.workflow_status || "not_linked");
  const currentStep = workflowContext?.current_step && typeof workflowContext.current_step === "object" ? workflowContext.current_step : {};
  const currentStepName =
    clean(currentStep.name) ||
    clean(currentStep.step_key) ||
    clean(task.workflow_current_step) ||
    (status === "not_linked" ? "Manual Execution" : "Current Step Unknown");
  const currentStepStatus =
    clean(workflowContext?.current_step_status) ||
    clean(currentStep.status) ||
    clean(task.workflow_current_step_status) ||
    clean(task.workflow_status) ||
    status;

  const recoveryState = clean(workflowContext?.recovery_state || task.workflow_recovery_state || (status === "not_linked" ? "manual" : "review_required"));
  const workflowId = clean(workflow.id || task.workflow_id || task.linked_workflow);

  const blockedDependencies = dependencies.filter((item) => {
    const itemStatus = clean(item.status).toLowerCase();
    return item.is_satisfied === false || ["blocked", "failed"].includes(itemStatus);
  });

  const pendingApprovals = approvals.filter((item) => {
    const itemStatus = clean(item.status).toLowerCase();
    return ["requested", "pending", "waiting"].includes(itemStatus);
  });

  const statusBadge = workflowLoading ? "Loading" : humanizeValue(status, "Not Linked");

  return (
    <>
      <ExecutionSection
        title="Workflow State"
        description="Task-scoped workflow state, current step, dependencies, approvals, and recovery context. No autonomous advancement occurs here."
        badge={statusBadge}
        badgeTone={statusTone(status)}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
          <ExecutionMetricTile label="Linked Workflow" value={workflowId || "Not Linked"} />
          <ExecutionMetricTile label="Current Step" value={humanizeValue(currentStepName)} />
          <ExecutionMetricTile label="Step Status" value={humanizeValue(currentStepStatus)} color={statusTone(currentStepStatus) === "danger" ? "#f87171" : "#60a5fa"} />
          <ExecutionMetricTile label="Recovery" value={humanizeValue(recoveryState)} color={statusTone(recoveryState) === "danger" ? "#f87171" : "#facc15"} />
        </div>

        {workflowError ? (
          <p style={{ color: "#fca5a5", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
            <strong>Workflow load issue:</strong> {workflowError}
          </p>
        ) : null}

        <p style={{ color: "#93c5fd", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
          This tab is task-scoped. It shows workflow context related to this task/control/resource and does not retry, advance, approve, or close workflow state automatically.
        </p>

        <ExecutionActionRow
          actions={[
            {
              key: "refresh-workflow",
              label: workflowLoading ? "Refreshing…" : "Refresh Workflow",
              onClick: onRefreshWorkflow,
              disabled: workflowLoading || !onRefreshWorkflow,
              variant: "primary",
            },
            { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
            { key: "validation", label: "Open Validation", onClick: () => onOpenTab?.("validation") },
          ]}
        />
      </ExecutionSection>

      <ExecutionSection
        title="Workflow Execution Context"
        description="Linked workflow steps, dependencies, approvals, and recovery indicators for this work item."
        badge={`${steps.length} step(s)`}
        badgeTone={workflowId ? "ok" : "neutral"}
      >
        {workflowLoading && !workflowId ? (
          <ExecutionEmptyState title="Loading workflow context" message="OASYS is loading task-scoped workflow context for this work item." />
        ) : null}

        {!workflowLoading && !workflowId ? (
          <ExecutionEmptyState
            title="No linked workflow found"
            message="This task does not currently have a linked workflow. Operators can still use the guidance, validation, evidence, SOP, history, and decision tabs to continue manually."
          />
        ) : null}

        {workflowId ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
              <ExecutionMetricTile label="Dependencies" value={dependencies.length} color={blockedDependencies.length ? "#f87171" : "#34d399"} />
              <ExecutionMetricTile label="Approvals" value={approvals.length} color={pendingApprovals.length ? "#facc15" : "#34d399"} />
              <ExecutionMetricTile label="Timeline Events" value={timelineEvents.length} color="#60a5fa" />
            </div>

            {steps.length ? (
              <div style={{ display: "grid", gap: 12 }}>
                <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 16 }}>Workflow Steps</h4>
                {steps.slice(0, 6).map((step, index) => (
                  <ExecutionContextCard
                    key={displayValue(step.id || step.step_key, `workflow-step-${index}`)}
                    title={itemTitle(step, "Workflow Step")}
                    chips={[
                      { label: humanizeValue(step.status, "Step"), tone: statusTone(step.status) },
                      { label: humanizeValue(step.step_type, "Step Type"), tone: "default" },
                      { label: step.is_required === false ? "Optional" : "Required", tone: "neutral" },
                    ]}
                    meta={[
                      { label: "Step ID", value: step.id, wrap: true },
                      { label: "Owner", value: step.owner_role || step.owner_user_id, wrap: true },
                      { label: "Due", value: step.due_at, wrap: true },
                    ]}
                    context={itemContext(step)}
                    actions={[
                      { key: "validation", label: "Open Validation", onClick: () => onOpenTab?.("validation") },
                      { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
                    ]}
                  />
                ))}
              </div>
            ) : null}

            {dependencies.length || approvals.length ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
                <ExecutionContextCard
                  title="Dependencies"
                  chips={[
                    { label: `${dependencies.length} total`, tone: "default" },
                    { label: `${blockedDependencies.length} blocked`, tone: blockedDependencies.length ? "danger" : "ok" },
                  ]}
                  meta={dependencies.slice(0, 4).map((item) => ({
                    label: humanizeValue(item.dependency_type || item.dependency_key, "Dependency"),
                    value: dependencyStatus(item),
                    wrap: true,
                  }))}
                  context={blockedDependencies[0]?.blocking_reason || "No blocking workflow dependencies are currently reported."}
                  actions={[{ key: "history", label: "View History", onClick: () => onOpenTab?.("history") }]}
                />

                <ExecutionContextCard
                  title="Approvals"
                  chips={[
                    { label: `${approvals.length} total`, tone: "default" },
                    { label: `${pendingApprovals.length} pending`, tone: pendingApprovals.length ? "warn" : "ok" },
                  ]}
                  meta={approvals.slice(0, 4).map((item) => ({
                    label: humanizeValue(item.approval_key || item.approval_type, "Approval"),
                    value: humanizeValue(item.status, "Unknown"),
                    wrap: true,
                  }))}
                  context={pendingApprovals[0]?.request_reason || "No pending workflow approvals are currently reported."}
                  actions={[{ key: "history", label: "View History", onClick: () => onOpenTab?.("history") }]}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </ExecutionSection>

      <ExecutionSection title="Workflow Timeline" description="Recent workflow activity related to this task, newest first.">
        <ExecutionTimeline events={timelineEvents} />
      </ExecutionSection>
    </>
  );
}
